import { useEffect, useState } from "react";
import { api } from "./api.js";
import { tr } from "./i18n.js";
import { data, dataOra } from "./text.js";
import { STARI_TICHET, STARI_VIZITA, eticheta } from "./enums.js";

/**
 * „Azi" — ecranul cu care începe ziua.
 *
 * Până acum fiecare rol ateriza pe prima secțiune vizibilă, adică pe Portalul
 * clientului — inclusiv tehnicianul, care n-are ce căuta acolo. Un om care
 * deschide aplicația vrea să vadă ce are DE FĂCUT, nu un cuprins.
 *
 * Ecranul nu calculează nimic nou: cheamă exact rutele pe care le-ar deschide
 * el singur, una câte una, și le pune una lângă alta. De-aia nu poate ajunge să
 * spună altceva decât secțiunea din care vine cifra — aceeași regulă ca la
 * tabloul de bord, care cheamă rapoartele în loc să le recalculeze.
 *
 * Fiecare card se stinge singur dacă rolul n-are dreptul la ruta lui. Un refuz
 * de drept NU e o eroare: nu se arată bandă roșie, cardul pur și simplu nu
 * există pentru cine n-are ce vedea acolo.
 */

const AZI = () => new Date().toISOString().slice(0, 10);

/** Cheamă ruta, dar tratează refuzul de drept ca „nu am cardul ăsta". */
function useCard(cheama, dependinte = [], permis = true) {
  const [stare, setStare] = useState({ incarca: true, date: null, refuzat: false });
  useEffect(() => {
    let viu = true;
    /*
     * Ce n-avem voie să cerem nici nu se cere — dar rezultatul e ACELAȘI ca la
     * un refuz venit de la server: cartonașul dispare. Altfel ar rămâne pe
     * ecran, gol, iar „n-ai voie" s-ar citi ca „n-ai nimic de făcut".
     */
    // `null` = „încă nu știu dacă am voie": se AȘTEAPTĂ, nu se cere. Prima
    // variantă întorcea `true` cât timp răspunsul cu drepturi nu venise, deci
    // cererea pleca oricum la prima randare — exact ce trebuia evitat.
    if (permis === null) return () => { viu = false; };
    if (permis === false) { setStare({ incarca: false, date: null, refuzat: true }); return () => { viu = false; }; }
    cheama()
      .then((d) => { if (viu) setStare({ incarca: false, date: d, refuzat: false }); })
      .catch((e) => {
        // 403 e un răspuns corect, nu o defecțiune — și e permanent: nu se
        // schimbă dacă mai apeși o dată.
        const refuzat = e?.status === 403;
        if (viu) setStare({ incarca: false, date: null, refuzat });
      });
    return () => { viu = false; };
  }, dependinte);
  return stare;
}

function Card({ titlu, subtitlu, stare, gol, laDeschidere, actiune, children }) {
  if (stare.refuzat) return null;
  return (
    <div className="panou">
      <div className="panou-antet">
        <span>{titlu}</span>
        {laDeschidere && (
          <button className="buton buton-mic buton-sters" onClick={laDeschidere}>
            {actiune ?? tr("deschide")}
          </button>
        )}
      </div>
      <div className="panou-corp">
        {subtitlu && <p className="subtitlu" style={{ marginTop: 0 }}>{subtitlu}</p>}
        {stare.incarca
          ? <div className="gol">{tr("Se încarcă…")}</div>
          : (stare.date == null || (Array.isArray(stare.date) && stare.date.length === 0))
            ? <div className="gol">{gol}</div>
            : children}
      </div>
    </div>
  );
}

export default function Azi({ eu, mergiLa }) {
  const azi = AZI();

  /*
   * Ecranul își întreabă întâi ce are voie să ceară.
   *
   * Cartonașele refuzate DISPĂREAU deja, corect — dar abia după ce cereau și
   * luau 403. Opt cereri la fiecare deschidere, dintre care câteva sortite
   * refuzului: un tehnician cerea „de facturat" și „oferte de programat" de
   * fiecare dată, degeaba. Nu se vedea pe ecran, se vedea în consolă — și de
   * acolo a ieșit, dintr-un parcurs prin interfață.
   *
   * `null` cât timp răspunsul n-a venit: se cere TOT, ca la prima încărcare
   * ecranul să nu pară gol. Iar dacă întrebarea însăși pică, rămâne `null` —
   * un ecran care nu știe ce drepturi ai cere ca înainte, iar serverul decide,
   * ca întotdeauna. Îngustarea de aici e o politețe, nu o măsură de
   * securitate: aceea stă în gardă și în politicile din bază.
   */
  const [drepturi, setDrepturi] = useState({ gata: false, harta: null });
  useEffect(() => {
    let viu = true;
    api.drepturileMele()
      .then((d) => { if (viu) setDrepturi({ gata: true, harta: d }); })
      // Dacă întrebarea însăși pică, se cere TOT, ca înainte: serverul decide.
      .catch(() => { if (viu) setDrepturi({ gata: true, harta: null }); });
    return () => { viu = false; };
  }, []);
  const pot = (modul) => (!drepturi.gata
    ? null
    : (drepturi.harta === null || (drepturi.harta[modul] ?? []).includes("citire")));

  // ── Ce am eu de făcut azi (doar dacă am fișă de tehnician) ───────────────
  const meleAzi = useCard(
    () => (eu.tehnicianId
      ? api.vizite({ tehnician: "eu", deLa: azi, panaLa: azi }).then((r) => r.vizite)
      : Promise.resolve(null)),
    [eu.tehnicianId, azi, drepturi], pot("interventii"),
  );

  // ── Tichete deschise, cel mai aproape de termen primul ───────────────────
  const tichete = useCard(
    () => api.tichete({ deschise: true }).then((r) => r.tichete), [drepturi], pot("tichete"));

  // ── Vizitele firmei, azi ────────────────────────────────────────────────
  const vizite = useCard(
    () => api.vizite({ deLa: azi, panaLa: azi }).then((r) => r.vizite),
    [azi, drepturi], pot("interventii"));

  // ── Ce e sub prag la stoc ───────────────────────────────────────────────
  const stoc = useCard(() => api.deReaprovizionat(), [drepturi], pot("stoc"));

  // ── Ce e de predat la facturare ─────────────────────────────────────────
  const facturare = useCard(() => api.deFacturat({}), [drepturi], pot("facturare"));

  // ── Oferte acceptate care așteaptă să fie programate ─────────────────────
  const deProgramat = useCard(() => api.ofertaDeProgramat(), [drepturi], pot("oferte"));

  // ── Restanțe deschise, cele mai apăsătoare ──────────────────────────────
  const restante = useCard(
    () => api.restante({ status: "Deschisa" }), [drepturi], pot("restante"));

  // ── Scadențe de mentenanță în 30 de zile ────────────────────────────────
  const scadente = useCard(() => api.scadentarMentenanta(30), [drepturi], pot("preventiv"));

  /*
   * Ceasul cel mai strâns al unui tichet. Starea ceasurilor se calculează pe
   * server, la citire; aici doar se alege cel care expiră primul, ca lista să
   * poată fi citită de sus în jos.
   */
  const celMaiStrans = (t) => {
    const deschise = (t.ceasuri ?? []).filter((c) => c.termenLa && !c.opritLa);
    if (!deschise.length) return null;
    return deschise.reduce((a, b) => (new Date(a.termenLa) < new Date(b.termenLa) ? a : b));
  };
  const dupaTermen = (a, b) => {
    const ca = celMaiStrans(a); const cb = celMaiStrans(b);
    if (!ca && !cb) return 0;
    if (!ca) return 1;
    if (!cb) return -1;
    return new Date(ca.termenLa) - new Date(cb.termenLa);
  };

  const primele = (l, n) => (Array.isArray(l) ? l.slice(0, n) : []);

  return (
    <div className="continut">
      <h1>{tr("Azi")}</h1>
      <p className="subtitlu">
        {tr("Ce ai de făcut, adunat din secțiunile pe care le-ai deschide oricum. Fiecare card duce în secțiunea lui, unde se și lucrează.")}
      </p>

      {eu.tehnicianId && (
        <Card
          titlu={tr("Vizitele mele, azi")}
          stare={meleAzi}
          gol={tr("Nicio vizită programată azi pe numele tău.")}
          laDeschidere={() => mergiLa("interventii")}
          actiune={tr("toate vizitele")}
        >
          <table className="tabel">
            <thead><tr>
              <th>{tr("Număr")}</th><th>{tr("Client")}</th><th>{tr("Locație")}</th>
              <th>{tr("Ora")}</th><th>{tr("Stare")}</th>
            </tr></thead>
            <tbody>
              {primele(meleAzi.date, 10).map((v) => (
                <tr key={v.id}>
                  <td><span className="cod-mic">{v.numar}</span></td>
                  <td>{tr(v.client?.denumire) ?? "—"}</td>
                  <td>{tr(v.locatie?.denumire) ?? "—"}</td>
                  <td>{v.oraProgramata || "—"}</td>
                  <td><span className="pastila pastila-soft">
                    {eticheta(STARI_VIZITA, v.status)}
                  </span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card
        titlu={tr("Tichete deschise")}
        subtitlu={tr("Cel mai aproape de termen, primul.")}
        stare={tichete}
        gol={tr("Niciun tichet deschis.")}
        laDeschidere={() => mergiLa("tichete")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Număr")}</th><th>{tr("Titlu")}</th><th>{tr("Client")}</th>
            <th>{tr("Stare")}</th><th>{tr("Termen")}</th>
          </tr></thead>
          <tbody>
            {primele([...(tichete.date ?? [])].sort(dupaTermen), 8).map((t) => {
              const c = celMaiStrans(t);
              return (
                <tr key={t.id}>
                  <td><span className="cod-mic">{t.numar}</span></td>
                  <td>{tr(t.titlu)}</td>
                  <td>{tr(t.client?.denumire) ?? "—"}</td>
                  <td><span className="pastila pastila-soft">
                    {eticheta(STARI_TICHET, t.status)}
                  </span></td>
                  <td className={c?.depasit ? "vechime-60" : undefined}>
                    {c ? dataOra(c.termenLa) : "—"}
                    {c?.depasit && ` · ${tr("depășit")}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card
        titlu={tr("Vizitele firmei, azi")}
        stare={vizite}
        gol={tr("Nicio vizită programată azi.")}
        laDeschidere={() => mergiLa("interventii")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Număr")}</th><th>{tr("Client")}</th><th>{tr("Ora")}</th>
            <th>{tr("Stare")}</th><th>{tr("Fișă")}</th>
          </tr></thead>
          <tbody>
            {primele(vizite.date, 8).map((v) => (
              <tr key={v.id}>
                <td><span className="cod-mic">{v.numar}</span></td>
                <td>{tr(v.client?.denumire) ?? "—"}</td>
                <td>{v.oraProgramata || "—"}</td>
                <td><span className="pastila pastila-soft">
                  {eticheta(STARI_VIZITA, v.status)}
                </span></td>
                <td>{v.fisa?.numar
                  ? <span className="cod-mic">{v.fisa.numar}</span>
                  : <span className="fara-pret">{tr("nedeschisă")}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        titlu={tr("Sub prag la stoc")}
        stare={stoc}
        gol={tr("Nimic sub prag.")}
        laDeschidere={() => mergiLa("stoc")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Articol")}</th><th>{tr("Gestiune")}</th>
            <th className="num">{tr("Sold")}</th><th className="num">{tr("Prag")}</th>
          </tr></thead>
          <tbody>
            {primele(stoc.date, 8).map((r, i) => (
              <tr key={r.id ?? i}>
                <td>{tr(r.articol?.denumire) ?? r.denumire ?? "—"}</td>
                <td>{tr(r.gestiune?.denumire) ?? "—"}</td>
                <td className="num">{r.cantitate ?? "—"}</td>
                <td className="num">{r.pragMinim ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        titlu={tr("De predat la facturare")}
        stare={facturare}
        gol={tr("Nicio fișă de predat.")}
        laDeschidere={() => mergiLa("facturare")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Fișă")}</th><th>{tr("Client")}</th><th>{tr("Data")}</th><th />
          </tr></thead>
          <tbody>
            {primele(facturare.date, 8).map((f) => (
              <tr key={f.fisaId}>
                <td><span className="cod-mic">{f.numar}</span></td>
                <td>{tr(f.client?.denumire) ?? "—"}</td>
                <td>{data(f.data)}</td>
                {/* O fișă acoperită integral de garanție sau abonament are
                    total zero. Nu se ascunde: a costat, doar nu se facturează. */}
                <td>{f.deFacturat
                  ? <span className="pastila pastila-soft">{tr("de facturat")}</span>
                  : <span className="fara-pret">{tr("fără valoare de facturat")}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        titlu={tr("Oferte acceptate, de programat")}
        subtitlu={tr("Clientul a spus da. Lucrarea nu se programează singură — nimeni n-a convenit încă o dată.")}
        stare={deProgramat}
        gol={tr("Nicio ofertă acceptată care să aștepte programare.")}
        laDeschidere={() => mergiLa("oferte")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Ofertă")}</th><th>{tr("Client")}</th>
            <th>{tr("Acceptată la")}</th><th>{tr("De cine")}</th>
          </tr></thead>
          <tbody>
            {primele(deProgramat.date, 6).map((o) => (
              <tr key={o.id}>
                <td><span className="cod-mic">{o.numar}</span>{tr(o.titlu)}</td>
                <td>{tr(o.client?.denumire) ?? "—"}</td>
                <td>{data(o.decisLa)}</td>
                <td>{o.decisDeNume ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        titlu={tr("Restanțe deschise")}
        subtitlu={tr("Scorul crește cu vechimea: o restanță minoră veche ajunge să conteze mai mult decât una importantă de ieri.")}
        stare={restante}
        gol={tr("Nicio restanță deschisă.")}
        laDeschidere={() => mergiLa("restante")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Ce")}</th><th>{tr("Client")}</th>
            <th className="num">{tr("Vechime")}</th><th className="num">{tr("Scor")}</th>
          </tr></thead>
          <tbody>
            {primele(restante.date, 6).map((r) => (
              <tr key={r.id}>
                <td>{tr(r.descriere)}</td>
                <td>{tr(r.client?.denumire) ?? "—"}</td>
                <td className="num">{r.vechimeZile != null ? tr("{0} zile", r.vechimeZile) : "—"}</td>
                <td className="num">{r.scorPrioritate ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        titlu={tr("Mentenanță scadentă în 30 de zile")}
        stare={scadente}
        gol={tr("Nicio scadență în următoarele 30 de zile.")}
        laDeschidere={() => mergiLa("preventiv")}
      >
        <table className="tabel">
          <thead><tr>
            <th>{tr("Sistem")}</th><th>{tr("Client")}</th><th>{tr("Scadent")}</th>
            <th className="num">{tr("Peste")}</th>
          </tr></thead>
          <tbody>
            {primele(scadente.date, 6).map((p) => (
              <tr key={p.id}>
                <td>{tr(p.sistemInstalat?.denumire) ?? "—"}</td>
                <td>{tr(p.client?.denumire) ?? "—"}</td>
                <td>{data(p.urmatoareaScadenta)}</td>
                <td className={"num" + (p.eScadent ? " vechime-60" : "")}>
                  {p.zilePanaLaScadenta < 0
                    ? tr("întârziat cu {0} zile", Math.abs(p.zilePanaLaScadenta))
                    : tr("{0} zile", p.zilePanaLaScadenta)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
